import {IRtcViewer} from "./interfaces/IRtcViewer";
import {Observable, Subscription, take} from "rxjs";
import {IceServerConfig} from "../shared/models/ice-server-config";
import {IMqttSignalingClient} from "./interfaces/IMqttSignalingClient";

/**
 * Callback interfaces for emitting MediaStreams to external consumers
 */
export interface RTCViewerStreamCallbacks {
    /** Called when remote video stream is available */
    onRemoteVideoStream?: (stream: MediaStream) => void;
    /** Called when remote audio stream is available */
    onRemoteAudioStream?: (stream: MediaStream) => void;
    /** Called when autoplay is blocked by the browser and user interaction is needed */
    onAutoplayBlocked?: () => void;
    /** Called when local audio stream (microphone) is acquired */
    onLocalAudioStream?: (stream: MediaStream) => void;
    /** Called when local video stream (camera) is enabled/disabled */
    onLocalVideoStream?: (stream: MediaStream | null) => void;
}

export class RTCViewer implements IRtcViewer {

    // private dataChannel: RTCDataChannel | null = null;
    private peerConnection: RTCPeerConnection | null = null;
    private localAudioStream: MediaStream | null = null;
    private localVideoTrack: MediaStreamTrack | null = null;
    private videoTrackEnabled: boolean = false;
    private videoTransceiver: RTCRtpTransceiver | null = null;
    private ownsLocalAudioStream: boolean = false; // True if we acquired the audio stream ourselves
    private signalingClientSubscriptions: Subscription[] = [];
    private endedEmitted = false;

    private remoteIceBuffer: RTCIceCandidateInit[] = [];
    private localIceBuffer: RTCIceCandidate[] = [];
    private canSendLocalIce = !this.answerer; // Offerer: send immediately. Answerer: wait for offer.

    private videoTrack: MediaStreamTrack | null = null;
    private audioTrack: MediaStreamTrack | null = null;

    private audioElement: HTMLAudioElement | null = null;

    /** Stream callbacks for emitting MediaStreams to external consumers */
    streamCallbacks: RTCViewerStreamCallbacks = {};

    /**
     * @param signalingClient - The signaling client for WebRTC negotiation
     * @param iceServerConfig$ - Observable for ICE server configuration
     * @param answerer - Whether this viewer is answering an incoming call
     * @param sharedLocalAudioStreamPromise - Optional Promise for shared local audio stream (resolves to null if no mic)
     * @param canSendVideo - Whether this viewer can send local video (sendrecv vs recvonly)
     * @param sharedLocalVideoStreamPromise - Optional Promise for shared local video stream (camera)
     */
    constructor(private readonly signalingClient: IMqttSignalingClient,
                private readonly iceServerConfig$: Observable<IceServerConfig>,
                private answerer: boolean = false,
                private sharedLocalAudioStreamPromise: Promise<MediaStream | null> | null = null,
                private readonly canSendVideo: boolean = false,
                private sharedLocalVideoStreamPromise: Promise<MediaStream | null> | null = null) { }

    async start() {
        if (this.peerConnection != null) {
            return Promise.resolve();
        }
        return new Promise<void>((resolve, reject) => {
            this.iceServerConfig$.pipe(take(1)).subscribe({
                next: async (config) => {
                    try {
                        await this.initPeerConnection(config);
                        resolve();
                    } catch (err) {
                        this.initializationError(err);
                        reject(err);
                    }
                },
                error: (err) => {
                    this.initializationError(err);
                    reject(err);
                }
            });
        });
    }

    stop() {
        this.closeCallSession();
    }

    hold() {
        this.setLocalMediaEnabled(false);
        this.setRemoteAudioEnabled(false);
    }

    resume() {
        this.setLocalMediaEnabled(true);
        // Respect the video track enabled state after re-enabling all senders
        if (this.localVideoTrack && !this.videoTrackEnabled) {
            this.localVideoTrack.enabled = false;
        }
        this.setRemoteAudioEnabled(true);
    }

    private setLocalMediaEnabled(enabled: boolean): void {
        if (!this.peerConnection) {
            return;
        }

        this.peerConnection.getSenders().forEach(sender => {
            if (sender.track && sender.track.enabled !== enabled) {
                sender.track.enabled = enabled;
            }
        });
    }

    /** Resume audio playback after user interaction (autoplay policy) */
    resumeAudio(): void {
        this.audioElement?.play().catch(() => {});
    }

     private setRemoteAudioEnabled(enabled: boolean): void {
        if (!this.peerConnection) {
            return;
        }

         this.peerConnection.getReceivers().forEach(receiver => {
             if (receiver.track.kind === "audio" && receiver.track.enabled !== enabled) {
                 receiver.track.enabled = enabled;
             }
         });
    }

    muteMicrophone() {
        if (this.localAudioStream) {
            this.localAudioStream.getAudioTracks().forEach(track => {
                if (track.enabled) track.enabled = false;
            });
        }
    }

    unmuteMicrophone() {
        if (this.localAudioStream) {
            this.localAudioStream.getAudioTracks().forEach(track => {
                if (!track.enabled) track.enabled = true;
            });
        }
    }

    muteAudio() {
        if (this.audioTrack && this.audioTrack.enabled) {
            this.audioTrack.enabled = false;
        }
    }

    unmuteAudio() {
        if (this.audioTrack && !this.audioTrack.enabled) {
            this.audioTrack.enabled = true;
        }
    }

    enableVideoTrack(): void {
        this.videoTrackEnabled = true;
        if (this.localVideoTrack) {
            this.localVideoTrack.enabled = true;
        }
    }

    disableVideoTrack(): void {
        this.videoTrackEnabled = false;
        if (this.localVideoTrack) {
            this.localVideoTrack.enabled = false;
        }
    }

    onCallSessionEnd?: () => void;

    onInitializationError?: () => void;

    private async initPeerConnection(config: IceServerConfig): Promise<void> {
        this.observeForSignalingMessages();
        this.signalingClient.open();

        const iceServers = [
            { urls: "stun:stun.kinesisvideo.eu-central-1.amazonaws.com:443" },
            ...(config.ice_server_list || []).map(server => ({
                urls: server.uris,
                username: server.username,
                credential: server.password
            }))
        ];

        this.peerConnection = new RTCPeerConnection({ iceServers });

        this.peerConnection.onconnectionstatechange = (e) => {
            if (this.peerConnection?.connectionState === "closed" || this.peerConnection?.connectionState === "disconnected") {
                console.log("Connection closed or disconnected");
                this.closeCallSession();
                this.onCallSessionEnd?.();
            }
        }

        this.peerConnection.oniceconnectionstatechange = () => {
            const state = this.peerConnection?.iceConnectionState;
            console.log("ICE connection state:", state);
            if (state === "failed" || state === "disconnected") {
                console.log("ICE connection failed or disconnected");
                this.closeCallSession();
                this.onCallSessionEnd?.();
            }
        }

        this.peerConnection.onicecandidate = (iceCandidate) => {
            if (!iceCandidate.candidate) return;

            const c = iceCandidate.candidate;

            if (this.canSendLocalIce) {
                this.signalingClient.sendIceCandidate(c);
            } else {
                this.localIceBuffer.push(c);
            }
        };

        this.peerConnection.ontrack = (trackEvent: RTCTrackEvent) => {
            const remoteStream = trackEvent.streams[0] ?? new MediaStream([trackEvent.track]);

            if (!remoteStream) return;

            if (trackEvent.track.kind === "video") {
                const track = remoteStream.getVideoTracks()[0];
                this.handleRemoteVideo(track, remoteStream);
                return;
            }

            if (trackEvent.track.kind === "audio") {
                const track = remoteStream.getAudioTracks()[0];
                this.handleRemoteAudio(track, remoteStream);
                return;
            }
        };

        if (!this.answerer) {
            await this.createAndSendOffer();
        }
    }

    private async createAndSendOffer() {
        try {
            // Video transceiver: sendrecv if we can send video, recvonly otherwise
            const direction = this.canSendVideo ? "sendrecv" : "recvonly";
            this.videoTransceiver = this.peerConnection!.addTransceiver("video", { direction });
            const h264 = RTCRtpReceiver
                .getCapabilities("video")!
                .codecs
                .filter(c => c.mimeType === "video/H264");
            this.videoTransceiver.setCodecPreferences(h264);

            // Add local video track to transceiver (disabled by default)
            if (this.canSendVideo && this.sharedLocalVideoStreamPromise) {
                const videoStream = await this.sharedLocalVideoStreamPromise;
                if (videoStream) {
                    const vTrack = videoStream.getVideoTracks()[0]?.clone();
                    if (vTrack) {
                        vTrack.enabled = this.videoTrackEnabled;
                        await this.videoTransceiver.sender.replaceTrack(vTrack);
                        this.localVideoTrack = vTrack;
                    }
                }
            }

            // Acquire audio stream (graceful - null means no mic available)
            await this.acquireLocalAudioStream();

            if (!this.peerConnection) {
                return;
            }

            // Add audio tracks if available (disabled by default)
            if (this.localAudioStream) {
                for (const track of this.localAudioStream.getAudioTracks()) {
                    if (track.enabled) {
                        track.enabled = false; // Mic disabled by default, enabled when talk starts
                    }
                    this.peerConnection!.addTrack(track);
                }
            } else {
                // No microphone: add recvonly audio transceiver so we can still hear remote audio
                this.peerConnection!.addTransceiver("audio", { direction: "recvonly" });
            }

            await this.peerConnection!.setLocalDescription(await this.peerConnection!.createOffer());
            this.signalingClient.sendOffer(this.peerConnection?.localDescription!);
        } catch (err) {
            this.initializationError(err);
        }
    }

    /**
     * Acquires the local audio stream. Uses the shared stream if available,
     * otherwise tries to acquire its own. Sets localAudioStream to null if
     * no microphone is available (call proceeds without local audio).
     */
    private async acquireLocalAudioStream(): Promise<void> {
        if (this.sharedLocalAudioStreamPromise) {
            const sharedStream = await this.sharedLocalAudioStreamPromise;
            if (sharedStream) {
                // Clone tracks so each viewer has independent enabled/disabled state
                this.localAudioStream = new MediaStream(sharedStream.getAudioTracks().map(t => t.clone()));
                this.ownsLocalAudioStream = true;
            } else {
                // Shared stream resolved to null (no mic available)
                this.localAudioStream = null;
            }
        } else {
            try {
                this.localAudioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
                this.ownsLocalAudioStream = true;
                this.streamCallbacks.onLocalAudioStream?.(this.localAudioStream);
            } catch (err) {
                console.warn("No microphone available, proceeding without local audio:", err);
                this.localAudioStream = null;
            }
        }
    }

    private observeForSignalingMessages() {
        let sdpSubscription: Subscription;
        if (this.answerer) {
            sdpSubscription = this.signalingClient.onSdpOffer().subscribe(
                async offer => {
                    if (!this.peerConnection) return;

                    try {
                        await this.peerConnection.setRemoteDescription(offer as any);
                        await this.flushRemoteIceBuffer();

                        this.canSendLocalIce = true;
                        this.flushLocalIceBuffer();

                        // Reuse video transceiver created by setRemoteDescription, or create one
                        this.videoTransceiver = this.peerConnection.getTransceivers()
                            .find(t => t.receiver.track?.kind === 'video') ?? null;
                        if (this.videoTransceiver) {
                            this.videoTransceiver.direction = this.canSendVideo ? "sendrecv" : "recvonly";
                            const h264 = RTCRtpReceiver
                                .getCapabilities("video")!
                                .codecs
                                .filter(c => c.mimeType === "video/H264");
                            this.videoTransceiver.setCodecPreferences(h264);
                        } else {
                            const direction = this.canSendVideo ? "sendrecv" : "recvonly";
                            this.videoTransceiver = this.peerConnection.addTransceiver("video", { direction });
                            const h264 = RTCRtpReceiver
                                .getCapabilities("video")!
                                .codecs
                                .filter(c => c.mimeType === "video/H264");
                            this.videoTransceiver.setCodecPreferences(h264);
                        }

                        // Add local video track to transceiver (disabled by default)
                        if (this.canSendVideo && this.videoTransceiver && this.sharedLocalVideoStreamPromise) {
                            const videoStream = await this.sharedLocalVideoStreamPromise;
                            if (videoStream) {
                                const vTrack = videoStream.getVideoTracks()[0]?.clone();
                                if (vTrack) {
                                    vTrack.enabled = this.videoTrackEnabled;
                                    await this.videoTransceiver.sender.replaceTrack(vTrack);
                                    this.localVideoTrack = vTrack;
                                }
                            }
                        }

                        // Acquire audio stream (graceful - null means no mic available)
                        await this.acquireLocalAudioStream();

                        // Add audio tracks if available (disabled by default)
                        if (this.localAudioStream) {
                            for (const track of this.localAudioStream.getAudioTracks()) {
                                if (track.enabled) {
                                    track.enabled = false; // Mic disabled by default, enabled when talk starts
                                }
                                this.peerConnection.addTrack(track);
                            }
                        }

                        const answer = await this.peerConnection.createAnswer();

                        await this.peerConnection.setLocalDescription(answer);

                        this.signalingClient.sendAnswer(this.peerConnection?.localDescription!);
                    } catch (err) {
                        this.initializationError(err);
                    }
                }
            );
        } else {
            sdpSubscription = this.signalingClient.onSdpAnswer().subscribe(
                answer => {
                    if (!this.peerConnection)
                        return;
                    this.peerConnection?.setRemoteDescription(answer as any).then(
                        () => this.flushRemoteIceBuffer());
                }
            );
        }

        const iceCandidateSubscription = this.signalingClient.onIceCandidate().subscribe(
            ice => {
                if (!this.peerConnection || !this.peerConnection.remoteDescription) {
                    this.remoteIceBuffer.push(ice);
                    return;
                }

                this.peerConnection?.addIceCandidate(ice as any);
            }
        );

        this.signalingClientSubscriptions.push(sdpSubscription, iceCandidateSubscription);
    }

    private async flushRemoteIceBuffer() {
        if (!this.peerConnection || !this.peerConnection.remoteDescription) {
            return;
        }

        for (const ice of this.remoteIceBuffer) {
            try {
                await this.peerConnection.addIceCandidate(ice);
            } catch (err) {
                console.error("Error applying buffered ICE candidate", err);
            }
        }

        this.remoteIceBuffer = [];
    }

    private flushLocalIceBuffer() {
        for (const ice of this.localIceBuffer) {
            this.signalingClient.sendIceCandidate(ice);
        }

        this.localIceBuffer = [];
    }

    private handleRemoteVideo(track: MediaStreamTrack, stream: MediaStream) {
        this.videoTrack = track;
        // Emit stream via callback instead of direct DOM attachment
        this.streamCallbacks.onRemoteVideoStream?.(stream);
    }

    private handleRemoteAudio(track: MediaStreamTrack, stream: MediaStream) {
        if (this.audioTrack) {
            this.audioTrack.stop();
        }
        this.audioTrack = track;

        // Emit stream via callback for external handling
        this.streamCallbacks.onRemoteAudioStream?.(stream);

        // Also maintain internal audio element for playback
        // (audio needs to play regardless of UI state)
        if (!this.audioElement) {
            this.audioElement = new Audio();
            this.audioElement.autoplay = true;
        }

        this.audioElement.srcObject = stream;
        this.audioElement.play().catch(err => {
            console.warn('Audio play failed, will retry on user interaction', err);
            this.streamCallbacks.onAutoplayBlocked?.();
        });
    }

    private closeCallSession() {
        if (this.endedEmitted) return;
        this.endedEmitted = true;

        for (const s of this.signalingClientSubscriptions) s.unsubscribe();
        this.signalingClientSubscriptions = [];
        this.signalingClient.close();

        this.peerConnection?.close();

        // Only stop local audio stream tracks if we own the stream (not shared)
        if (this.localAudioStream && this.ownsLocalAudioStream) {
            this.localAudioStream.getTracks().forEach(t => t.stop());
        }
        this.localAudioStream = null;

        // Stop cloned local video track
        if (this.localVideoTrack) {
            this.localVideoTrack.stop();
            this.localVideoTrack = null;
        }

        // Clean up audio element
        if (this.audioElement) {
            this.audioElement.srcObject = null;
            this.audioElement = null;
        }
    }

    private initializationError(err: any) {
        console.error(err);
        this.onCallSessionEnd = () => null;
        this.stop();
        this.onInitializationError ? this.onInitializationError() : null;
    }

}
